import { MagnifyingGlass, X } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import useMergedRef from "../../hooks/use-merged-ref.js";
import { Divider } from "../divider/Divider.js";
import { Flex } from "../flex/Flex.js";
import { IconButton } from "../icon-button/IconButton.js";
import { TextField, type TextFieldProps } from "../textfield/TextField.js";
import { Tooltip, type TooltipProps } from "../tooltip/Tooltip.js";

interface SearchProps extends Omit<TextFieldProps, "size"> {
  size?: "md" | "lg";
  tooltip?: boolean;
  tooltipProps?: Omit<TooltipProps, "content" | "children" | "disabled">;
  title?: string;
  classNames?: {
    root?: string;
    clearIcon?: string;
    divider?: string;
    voiceIcon?: string;
    rightSection?: string;
  };
}

const Search = React.forwardRef<HTMLInputElement, SearchProps>((props, ref) => {
  const {
    size = "md",
    value,
    defaultValue = "",
    onInput,
    onChange,
    placeholder = "Search",
    title = placeholder,
    classNames,
    tooltipProps,
    className,
    rightSection,
    tooltip = false,
    ...rest
  } = props;

  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchInputRef = useMergedRef(ref, inputRef);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    onChange?.(e);
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    setInternalValue(e.currentTarget.value);
    onInput?.(e);
  };

  const currentValue = value !== undefined ? value : internalValue;

  const isValueNonEmpty = (
    value: string | number | readonly string[],
  ): boolean => {
    if (typeof value === "string") {
      return value.length > 0;
    } else if (Array.isArray(value)) {
      return value.length > 0;
    }
    return false;
  };

  const handleClear = () => {
    if (inputRef.current) inputRef.current.value = "";
    if (value === undefined) setInternalValue("");
    onChange?.({
      target: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>);
    onInput?.({
      currentTarget: { value: "" },
    } as React.FormEvent<HTMLInputElement>);
  };

  return (
    <Tooltip
      content={internalValue}
      disabled={!tooltip || !currentValue}
      {...tooltipProps}
    >
      <TextField
        autoCorrect="off"
        type="text"
        ref={searchInputRef}
        placeholder={placeholder}
        className={clsx("ox-reset ox-SearchRoot", className, classNames?.root)}
        size={size === "md" ? "md" : "lg"}
        value={currentValue}
        onChange={handleChange}
        onInput={handleInput}
        title={title}
        leftSection={<MagnifyingGlass />}
        rightSection={
          <Flex gap={8} align="center" className={classNames?.rightSection}>
            {isValueNonEmpty(currentValue) ? (
              <IconButton
                className={clsx("ox-reset", classNames?.clearIcon)}
                onClick={handleClear}
                size="sm"
                variant="tertiary"
                color="gray"
              >
                <X />
              </IconButton>
            ) : null}
            {rightSection ? (
              <>
                {isValueNonEmpty(currentValue) && (
                  <Divider
                    className={clsx("ox-SearchDivider", classNames?.divider)}
                    orientation={"vertical"}
                  />
                )}
                {rightSection}
              </>
            ) : null}
          </Flex>
        }
        {...rest}
      />
    </Tooltip>
  );
});

Search.displayName = "Search";
export { Search };
export type { SearchProps };
